import React from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { Logo } from "./Logo";
import { COMPANY, NAV } from "../data/site";
import { LEGAL_NAV } from "../data/legal";

export const Footer = () => {
  return (
    <footer className="bg-[#0A0D12] border-t border-white/10 relative z-10" data-testid="site-footer">
      <div className="border-b border-white/10 bg-[#11151C]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-start gap-4" data-testid="licence-status-notice">
          <ShieldAlert className="text-[#C8A45A] shrink-0 mt-0.5" size={18} />
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#C8A45A] mb-2">Licence status — draft</p>
            <p className="text-xs text-[#A1A5AB] leading-relaxed max-w-5xl">{COMPANY.afslStatus}</p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Logo testId="footer-logo" />
            <p className="mt-6 text-[#A1A5AB] text-sm leading-relaxed max-w-sm">
              An Australian wholesale prime-of-prime. Once our AFS licence is granted, we will provide
              multi-asset CFD liquidity, execution technology and real-time risk analytics to professional desks.
            </p>
            <div className="mt-8">
              <span className="overline">{COMPANY.clientNotice}</span>
            </div>
          </div>

          <div className="md:col-span-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#737882] mb-5">Navigate</p>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-link-${n.label.toLowerCase()}`}
                    className="text-sm text-[#A1A5AB] hover:text-white transition-colors"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A45A] mb-5">Legal &amp; Regulatory</p>
            <ul className="space-y-3">
              {LEGAL_NAV.map((n) => (
                <li key={n.slug}>
                  <Link
                    to={`/legal/${n.slug}`}
                    data-testid={`footer-legal-${n.slug}`}
                    className="text-sm text-[#A1A5AB] hover:text-white transition-colors"
                  >
                    {n.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#737882] mb-5">Registered Office</p>
            <address className="not-italic text-sm text-[#A1A5AB] leading-relaxed">
              {COMPANY.legalName}
              <br />
              {COMPANY.addressLines.map((l) => (
                <span key={l}>
                  {l}
                  <br />
                </span>
              ))}
            </address>
            <div className="mt-5 space-y-1 text-sm">
              <a href={`mailto:${COMPANY.email}`} className="text-[#C8A45A] hover:text-[#D4B572] transition-colors block" data-testid="footer-email">
                {COMPANY.email}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10">
          <p className="text-xs text-[#737882] leading-relaxed max-w-5xl" data-testid="footer-legal-notice">
            {COMPANY.legalName} (ACN {COMPANY.acn}, ABN {COMPANY.abn}) is a company registered in {COMPANY.jurisdiction}.
            {" "}ET Prime is a wholesale-only business and, once licensed, will provide financial services only to wholesale, professional and
            eligible-counterparty clients as defined under the Corporations Act 2001 (Cth) and does not provide
            financial services to retail clients. The information on this website is general information only and is
            not personal advice, an offer or a solicitation. Where we provide financial services we act as principal
            and market maker. OTC contracts for difference (CFDs) and margin FX are leveraged products; you do not own
            the underlying and, as a wholesale client, you can lose more than the margin you deposit and are exposed to
            our credit risk. There is no negative-balance protection unless we agree one in writing. Prospective
            counterparties should obtain independent advice and complete their own due diligence.
          </p>
          <p className="text-xs text-[#737882] leading-relaxed max-w-5xl mt-4 font-mono">{COMPANY.footerLine}</p>
          <div className="mt-8">
            <p className="text-xs text-[#737882]">
              © {new Date().getFullYear()} {COMPANY.legalName}. Sydney, Australia. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
